import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import CardPaymentModal, { CardPaymentModalState } from '../components/CardPaymentModal';
import { ExistingCard, CardPaymentModalResult } from '../types/api';

interface CardPaymentModalContextValue {
  show: (title: string, existingCards?: ExistingCard[]) => Promise<CardPaymentModalResult>;
}

const CardPaymentModalContext = createContext<CardPaymentModalContextValue | null>(null);

export function CardPaymentModalProvider({ children }: { children: React.ReactNode }) {
  const [modalState, setModalState] = useState<CardPaymentModalState>({
    isOpen: false,
    title: '',
  });

  const resolveRef = useRef<((result: CardPaymentModalResult) => void) | null>(null);

  const show = useCallback((title: string, existingCards?: ExistingCard[]): Promise<CardPaymentModalResult> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModalState({ isOpen: true, title, existingCards });
    });
  }, []);

  const handleResolve = useCallback((result: CardPaymentModalResult) => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
    resolveRef.current?.(result);
    resolveRef.current = null;
  }, []);

  return (
    <CardPaymentModalContext.Provider value={{ show }}>
      {children}
      <CardPaymentModal state={modalState} onResolve={handleResolve} />
    </CardPaymentModalContext.Provider>
  );
}

export function useCardPaymentModal(): CardPaymentModalContextValue {
  const ctx = useContext(CardPaymentModalContext);
  if (!ctx) {
    throw new Error('useCardPaymentModal must be used within CardPaymentModalProvider');
  }
  return ctx;
}
